import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from 'recharts';

export const CampaignROASLeaderboardChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, entities, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance || !entities) return [];

        // Use detailedBreakdown which preserves original (non-remapped) entity IDs
        const breakdownSource = finance.detailedBreakdown || finance.breakdown || [];

        const campaignEntities = entities.filter(
            (e: any) => e.entityType === 'campaign'
        );
        if (campaignEntities.length === 0) return [];

        const campaignIds = new Set(campaignEntities.map((e: any) => e.id));
        const campaignNameMap: Record<string, string> = {};
        for (const e of campaignEntities) {
            campaignNameMap[e.id as string] = (e.name as string) || (e.id as string);
        }

        const spendByEntity: Record<string, number> = {};
        const revenueByEntity: Record<string, number> = {};

        for (const item of breakdownSource) {
            const entityId = (item.entityId || (item as any).parentEntityId) as string;
            if (!campaignIds.has(entityId)) continue;

            const value = Math.abs(Number(item.value) || 0);

            if (item.type === 'EXPENSE' && ['advertising', 'ad_spend', 'marketing'].includes(item.category)) {
                spendByEntity[entityId] = (spendByEntity[entityId] || 0) + value;
            } else if (item.type === 'REVENUE') {
                revenueByEntity[entityId] = (revenueByEntity[entityId] || 0) + value;
            }
        }

        const totalRevenue = Number(finance.totalRevenues) || 0;
        const totalAdSpend = Number(finance.metrics?.adSpend) || 0;

        const allCampaignIds = new Set([
            ...Object.keys(spendByEntity),
        ]);

        const data = Array.from(allCampaignIds).map((entityId) => {
            const spend = spendByEntity[entityId] || 0;
            // Use campaign's share of total ad spend to estimate its revenue contribution
            const revenueShare = totalAdSpend > 0 ? (spend / totalAdSpend) * totalRevenue : 0;
            const roas = spend > 0 ? Math.round((revenueShare / spend) * 100) / 100 : 0;

            return {
                name: campaignNameMap[entityId] || entityId,
                roas,
                spend,
                revenue: Math.round(revenueShare * 100) / 100,
            };
        });

        return data
            .sort((a, b) => b.roas - a.roas)
            .slice(0, 10);
    }, [finance?.detailedBreakdown, finance?.breakdown, finance?.totalRevenues, finance?.metrics?.adSpend, entities]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    const getBarColor = (roas: number): string => {
        if (roas >= 2) return CHART_COLORS.revenue;
        if (roas >= 1) return CHART_COLORS.accent;
        return CHART_COLORS.expense;
    };

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 80 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => `${value}x`}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={75}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number, _name: string, props: any) => {
                                const payload = props.payload as any;
                                return [
                                    `${value}x (${t('charts.spend', 'Spend')}: ${formatCompactNumber(payload.spend as number, currencySymbol)})`,
                                    t('charts.roas', 'ROAS'),
                                ];
                            }}
                        />
                        <Bar
                            dataKey="roas"
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(entry.roas)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
